import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  format,
  isSameDay,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { appendContexteValuesToUrl } from '../../utils/url';

/**
 * @typedef {'day' | 'week' | 'month' | null} ViewTypeSplitBy
 */

/**
 * @typedef {object} CalendarSplit
 * @property {Date} start - Block start date (initialDate for FullCalendar)
 * @property {string} title - Period label shown in the resource column header
 */

/**
 * @param {object} params
 * @param {ApiManagerTimelineDirection} params.direction
 * @param {ApiManagerTimelinePeriodType} params.periodType
 * @param {number} params.resourceCount
 * @returns {ApiManagerTimelineViewType}
 */
export function resolveMockViewType({ direction, periodType, resourceCount }) {
  if (direction === 'vertical') {
    switch (periodType) {
      case 'day':
        return 'agendaDay';
      case 'week':
        return 'agendaWeek';
      case 'month':
        return 'month';
      default:
        return 'agendaWeek';
    }
  }

  switch (periodType) {
    case 'day':
      return 'horizontalresourceDay';
    case 'week':
      return 'horizontalbyresourceDayWeek';
    case 'month':
      if (resourceCount === 1) return 'horizontalbyresourceDayMonth';
      return 'horizontalresourceMonth';
    default:
      return 'horizontalresourceDay';
  }
}

/**
 * @typedef {object} ViewTypeConfig
 * @property {string} fcView - FullCalendar base view name
 * @property {{ days?: number, weeks?: number, months?: number, years?: number }} duration
 * @property {ViewTypeSplitBy} splitBy - When set, render one calendar per day/week/month block
 * @property {boolean} usesSlotTimes - Whether slotMinTime/slotMaxTime apply
 * @property {boolean} hourHeaders - Whether to show hour headers
 * @property {boolean} dayColumns - One timeline column per day (no hourly slots)
 */

/** @type {Record<ApiManagerTimelineViewType, ViewTypeConfig>} */
export const VIEW_TYPE_CONFIG = {
  agendaDay: {
    fcView: 'timeGrid',
    duration: { days: 1 },
    splitBy: null,
    usesSlotTimes: true,
    hourHeaders: true,
    dayColumns: false,
  },
  agendaWeek: {
    fcView: 'timeGrid',
    duration: { weeks: 1 },
    splitBy: null,
    usesSlotTimes: true,
    hourHeaders: true,
    dayColumns: false,
  },
  month: {
    fcView: 'dayGridMonth',
    duration: { months: 1 },
    splitBy: null,
    usesSlotTimes: false,
    hourHeaders: false,
    dayColumns: false,
  },
  horizontalresourceDay: {
    fcView: 'resourceTimeline',
    duration: { days: 1 },
    splitBy: null,
    usesSlotTimes: true,
    hourHeaders: true,
    dayColumns: false,
  },
  horizontalbyresourceDayWeek: {
    fcView: 'resourceTimeline',
    duration: { days: 1 },
    splitBy: 'day',
    usesSlotTimes: true,
    hourHeaders: true,
    dayColumns: false,
  },
  horizontalresourceDayWeek: {
    fcView: 'resourceTimeline',
    duration: { days: 1 },
    splitBy: 'day',
    usesSlotTimes: true,
    hourHeaders: true,
    dayColumns: false,
  },
  horizontalresourceWeek: {
    fcView: 'resourceTimeline',
    duration: { weeks: 1 },
    splitBy: 'week',
    usesSlotTimes: false,
    hourHeaders: false,
    dayColumns: true,
  },
  horizontalbyresourceDayMonth: {
    fcView: 'resourceTimeline',
    duration: { days: 1 },
    splitBy: 'day',
    usesSlotTimes: true,
    hourHeaders: false,
    dayColumns: false,
  },
  horizontalresourceMonth: {
    fcView: 'resourceTimeline',
    duration: { months: 1 },
    splitBy: 'month',
    usesSlotTimes: false,
    hourHeaders: false,
    dayColumns: true,
  },
};

/**
 * @param {string | undefined | null} viewType
 * @returns {ViewTypeConfig}
 */
export function getViewTypeConfig(viewType) {
  if (!viewType || !VIEW_TYPE_CONFIG[/** @type {ApiManagerTimelineViewType} */ (viewType)]) {
    return VIEW_TYPE_CONFIG.horizontalresourceDay;
  }
  return VIEW_TYPE_CONFIG[/** @type {ApiManagerTimelineViewType} */ (viewType)];
}

/**
 * @param {Date} date
 * @returns {string}
 */
export function formatWeekSplitTitle(date) {
  const weekEnd = addDays(date, 6);
  const startLabel = format(date, 'd MMM yyyy', { locale: fr });
  const endLabel = format(weekEnd, 'd MMM yyyy', { locale: fr });
  return `${startLabel} – ${endLabel}`;
}

/**
 * @param {Date} date
 * @returns {string}
 */
export function formatMonthSplitTitle(date) {
  return format(date, 'MMMM yyyy', { locale: fr });
}

/**
 * Day column header for stacked programming views (e.g. "L29").
 * @param {Date} date
 * @returns {string}
 */
export function formatDayColumnSlotLabel(date) {
  if (!date || !isValid(date)) {
    return '';
  }
  const weekdayLetter = format(date, 'EEEE', { locale: fr }).charAt(0).toUpperCase();
  const dayNumber = format(date, 'd', { locale: fr });
  return `${weekdayLetter}${dayNumber}`;
}

/**
 * @param {{ date: Date | { marker: Date } }} arg
 * @returns {string}
 */
export function formatDayColumnSlotLabelForFullCalendar(arg) {
  const rawDate = arg.date;
  const date = rawDate instanceof Date ? rawDate : new Date(rawDate.marker);
  return formatDayColumnSlotLabel(date);
}

/**
 * Build stacked calendar blocks for week/month programming views.
 * @param {string} dateStart - ISO date (inclusive)
 * @param {string} dateEnd - ISO date (inclusive)
 * @param {'week' | 'month'} splitBy
 * @returns {CalendarSplit[]}
 */
export function buildCalendarSplits(dateStart, dateEnd, splitBy) {
  if (!dateStart || !dateEnd || !splitBy) {
    return [];
  }

  const rangeEnd = parseISO(dateEnd);
  if (!isValid(rangeEnd)) {
    return [];
  }

  /** @type {CalendarSplit[]} */
  const splits = [];

  if (splitBy === 'week') {
    let blockStart = parseISO(dateStart);
    if (!isValid(blockStart)) {
      return [];
    }
    while (blockStart <= rangeEnd) {
      splits.push({
        start: blockStart,
        title: formatWeekSplitTitle(blockStart),
      });
      blockStart = addDays(blockStart, 7);
    }
    return splits;
  }

  let blockStart = startOfMonth(parseISO(dateStart));
  if (!isValid(blockStart)) {
    return [];
  }
  while (blockStart <= rangeEnd) {
    splits.push({
      start: blockStart,
      title: formatMonthSplitTitle(blockStart),
    });
    blockStart = addMonths(blockStart, 1);
  }
  return splits;
}

/**
 * @param {string} time
 * @returns {string}
 */
export function formatApiTimeForFullCalendar(time) {
  if (!time) return '00:00:00';
  const [hours, minutes = '00'] = time.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
}

/**
 * @param {ApiManagerTimelineResource} resource
 * @returns {CalendarResource}
 */
export function mapTimelineResourceToCalendarResource(resource) {
  return {
    id: resource.id,
    title: resource.lib,
    ...(resource.section ? { section: resource.section } : {}),
    extendedProps: {
      type: resource.type,
      section: resource.section,
      backgroundColor: resource.backgroundColor,
      editable: resource.editable,
      mainUser: resource.mainUser,
    },
  };
}

/**
 * Keep yyyy-MM-ddTHH:mm:ss digits from an API datetime, dropping offset / Z / ms.
 * Date-only values are returned unchanged.
 * @param {string} value
 * @returns {string}
 */
function stripTimelineDateTimeOffset(value) {
  if (!value) {
    return value;
  }
  const match = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : value;
}

/**
 * @param {Date} date
 * @returns {string}
 */
function formatTimelineLocalInstant(date) {
  return format(date, 'yyyy-MM-dd\'T\'HH:mm:ss');
}

/**
 * @param {string} wallClockIso
 * @param {Date} calendarDay
 * @returns {Date}
 */
function combineWallClockWithCalendarDay(wallClockIso, calendarDay) {
  const wallClock = parseISO(wallClockIso);
  if (!isValid(wallClock) || !isValid(calendarDay)) {
    return calendarDay;
  }
  const dayStart = startOfDay(calendarDay);
  return new Date(
    dayStart.getFullYear(),
    dayStart.getMonth(),
    dayStart.getDate(),
    wallClock.getHours(),
    wallClock.getMinutes(),
    wallClock.getSeconds(),
    wallClock.getMilliseconds(),
  );
}

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {{ start: Date | null, end: Date | null }}
 */
function resolveFastEditEventInstants(event) {
  const displayStart = event.extendedProps?.displayStart;
  const displayEnd = event.extendedProps?.displayEnd;
  if (!displayStart || !event.start) {
    return {
      start: event.start,
      end: event.end ?? event.start,
    };
  }

  const displayEndIso = displayEnd ?? displayStart;
  const displayStartInstant = parseISO(displayStart);
  const displayEndInstant = parseISO(displayEndIso);
  const dayOffset = isValid(displayStartInstant) && isValid(displayEndInstant)
    ? differenceInCalendarDays(startOfDay(displayEndInstant), startOfDay(displayStartInstant))
    : 0;

  return {
    start: combineWallClockWithCalendarDay(displayStart, event.start),
    end: combineWallClockWithCalendarDay(
      displayEndIso,
      addDays(startOfDay(event.start), dayOffset),
    ),
  };
}

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {{ start: Date | null, end: Date | null }}
 */
function resolveEventFocusClockInstants(event) {
  const displayStart = event.extendedProps?.displayStart;
  const displayEnd = event.extendedProps?.displayEnd;
  if (displayStart) {
    const start = parseISO(displayStart);
    const end = displayEnd ? parseISO(displayEnd) : null;
    return {
      start: isValid(start) ? start : event.start,
      end: end && isValid(end) ? end : event.end ?? event.start,
    };
  }
  return {
    start: event.start,
    end: event.end ?? event.start,
  };
}

/**
 * Stretch timed events to full calendar days for day-column timeline views.
 * @param {string} apiStart
 * @param {string} apiEnd
 * @returns {{ start: string, end: string, displayStart: string, displayEnd: string } | null}
 */
function stretchTimelineEventForDayColumns(apiStart, apiEnd) {
  const startInstant = parseISO(apiStart);
  const endInstant = parseISO(apiEnd);
  if (!isValid(startInstant) || !isValid(endInstant)) {
    return null;
  }

  const visualStart = startOfDay(startInstant);
  const visualEndExclusive = addDays(visualStart, 1);

  return {
    start: formatTimelineLocalInstant(visualStart),
    end: formatTimelineLocalInstant(visualEndExclusive),
    displayStart: apiStart,
    displayEnd: apiEnd,
  };
}

/**
 * @param {ApiManagerTimelineEvent} event
 * @param {boolean} [dayColumns=false]
 * @returns {CalendarEvent}
 */
export function mapTimelineEventToCalendarEvent(event, dayColumns = false) {
  const resourceIds = event.resourceIds ?? [];
  const start = stripTimelineDateTimeOffset(event.start);
  const end = stripTimelineDateTimeOffset(event.end);
  const shouldStretch = dayColumns && !event.allDay;
  const stretched = shouldStretch
    ? stretchTimelineEventForDayColumns(start, end)
    : null;

  return {
    id: event.id,
    title: event.lib,
    start: stretched?.start ?? start,
    end: stretched?.end ?? end,
    allDay: event.allDay,
    editable: event.editable,
    ...(resourceIds.length === 1 ? { resourceId: resourceIds[0] } : {}),
    ...(resourceIds.length > 1 ? { resourceIds } : {}),
    color: event.backgroundColor ?? undefined,
    extendedProps: {
      editable: event.editable,
      fastEditUrl: event.fastEditUrl,
      icon: event.icon,
      iconColor: event.iconColor,
      textDecoration: event.textDecoration,
      recurrence: event.recurrence,
      hoverDetailsUrl: event.hoverDetailsUrl,
      actions: event.actions,
      isMultiResource: resourceIds.length > 1,
      ...(stretched
        ? {
          displayStart: stretched.displayStart,
          displayEnd: stretched.displayEnd,
        }
        : {}),
    },
  };
}

/**
 * @param {ApiManagerTimelineFilter | undefined | null} timelineFilters
 * @returns {CalendarResourceAreaColumn[]}
 */
export function buildTimelineResourceAreaColumns(timelineFilters) {
  const groupField = timelineFilters?.groupBy?.selectedOption === 'type' ? 'type' : 'title';

  return [
    {
      field: groupField,
      headerContent: groupField === 'type' ? 'Type' : 'Ressource',
    },
  ];
}

/**
 * @param {ApiManagerTimeline} timeline
 * @param {ApiManagerTimelineFilter | undefined | null} [timelineFilters]
 * @returns {FullCalendarData}
 */
export function mapTimelineToFullCalendarData(timeline, timelineFilters = null) {
  const viewConfig = getViewTypeConfig(timeline.viewType);

  return {
    viewType: timeline.viewType,
    dateStart: stripTimelineDateTimeOffset(timeline.dateStart),
    dateEnd: stripTimelineDateTimeOffset(timeline.dateEnd),
    dateFilter: stripTimelineDateTimeOffset(timeline.dateFilter),
    minTime: timeline.minTime,
    maxTime: timeline.maxTime,
    slotMinutes: timeline.slotMinutes,
    exportUrls: timeline.exportUrls,
    addFormUrls: timeline.addFormUrls,
    events: timeline.events.map(
      (event) => mapTimelineEventToCalendarEvent(event, viewConfig.dayColumns),
    ),
    resources: timeline.resources.map(mapTimelineResourceToCalendarResource),
    resourcesAreaColumns: buildTimelineResourceAreaColumns(timelineFilters),
    viewConfig,
  };
}

/**
 * @param {ApiManagerTimelineFilter | undefined} filters
 * @returns {object | null}
 */
export function getTimelineFiltersKey(filters) {
  if (!filters) return null;

  return {
    dateRange: {
      type: filters.dateRange.type,
      selectedDate: filters.dateRange.selectedDate,
      rangeStartDate: filters.dateRange.rangeStartDate,
      rangeEndDate: filters.dateRange.rangeEndDate,
    },
    direction: filters.direction.selected,
    resourceIds: filters.resources.selectedIds.join(','),
    filterValues: JSON.stringify(filters.filterValues),
    groupBy: filters.groupBy?.selectedOption ?? null,
    personalViews: filters.personalViews.selectedOption ?? null,
    timeWindow: `${filters.timeWindow.minTime}-${filters.timeWindow.maxTime}`,
  };
}

/**
 * @param {ApiManagerTimelineFilter} filters
 * @returns {ApiManagerTimelineFiltersCreate}
 */
export function buildTimelineFiltersPostPayload(filters) {
  return {
    dateRange: {
      type: filters.dateRange.type,
      selectedDate: filters.dateRange.selectedDate,
      rangeStartDate: filters.dateRange.rangeStartDate,
      rangeEndDate: filters.dateRange.rangeEndDate,
    },
    direction: filters.direction.selected,
    resourceIds: filters.resources.selectedIds,
    resourceGroupId: filters.resourceGroups.selectedId,
    timeWindow: {
      minTime: filters.timeWindow.minTime,
      maxTime: filters.timeWindow.maxTime,
    },
    filterValues: filters.filterValues,
    groupBy: {
      selectedOption: filters.groupBy?.selectedOption ?? null,
    },
    personalViews: {
      selectedOption: filters.personalViews.selectedOption ?? null,
    },
  };
}

/**
 * @param {ApiManagerTimelineFilter} filters
 * @param {Partial<ApiManagerTimelineFiltersCreate>} overrides
 * @returns {ApiManagerTimelineFiltersCreate}
 */
export function buildTimelineFiltersPostPayloadWithOverrides(filters, overrides = {}) {
  const basePayload = buildTimelineFiltersPostPayload(filters);

  return {
    ...basePayload,
    ...overrides,
    dateRange: {
      ...basePayload.dateRange,
      ...overrides.dateRange,
    },
    timeWindow: {
      ...basePayload.timeWindow,
      ...overrides.timeWindow,
    },
    groupBy: {
      ...basePayload.groupBy,
      ...overrides.groupBy,
    },
    personalViews: {
      ...basePayload.personalViews,
      ...overrides.personalViews,
    },
  };
}

/**
 * Converts API maxTime above 24h to picker value (e.g. 26:00 -> 02:00).
 * @param {string} maxTime
 * @returns {string}
 */
export function apiMaxTimeToPickerValue(maxTime) {
  const [hours, minutes = '00'] = maxTime.split(':');
  const hoursInt = Number(hours);
  if (hoursInt >= 24) {
    return `${String(hoursInt - 24).padStart(2, '0')}:${minutes}`;
  }
  return maxTime;
}

/**
 * @param {number} slotMinutes
 * @returns {string}
 */
export function slotMinutesToDuration(slotMinutes) {
  const hours = Math.floor(slotMinutes / 60);
  const minutes = slotMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}

/**
 * Whether the timeline orientation switch should be shown based on API direction options.
 * @param {ApiManagerTimelineDirectionFilter | undefined | null} direction
 * @returns {boolean}
 */
export function shouldShowTimelineOrientationSwitch(direction) {
  return (direction?.options?.length ?? 0) >= 2;
}

/**
 * @param {string} duration - FullCalendar slot duration (HH:mm:ss)
 * @returns {number}
 */
export function durationToSlotMinutes(duration) {
  const [hours = '0', minutes = '0'] = duration.split(':');
  return Number(hours) * 60 + Number(minutes);
}

/**
 * Replace or append a filter value by id, preserving other entries.
 * @param {Array<ApiManagerFilterValues>} filterValues
 * @param {ApiManagerFilterValues} nextValue
 * @returns {Array<ApiManagerFilterValues>}
 */
export function mergeFilterValues(filterValues, nextValue) {
  const withoutCurrent = filterValues.filter((filter) => filter.id !== nextValue.id);
  return [...withoutCurrent, nextValue];
}

/**
 * @param {string} template
 * @param {Record<string, string>} values
 * @returns {string}
 */
function interpolateLabel(template, values) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replace(`{{${key}}}`, value),
    template,
  );
}

/**
 * @param {Date | null | undefined} date
 * @returns {string}
 */
function formatEventFocusClockTime(date) {
  if (!date || !isValid(date)) {
    return '';
  }
  return format(date, 'HH:mm');
}

/**
 * @param {Date} start
 * @param {Date | null | undefined} end
 * @param {EventFocusLabels} labels
 * @returns {string}
 */
function formatEventFocusDateLabel(start, end, labels) {
  const startDate = format(start, 'd MMM', { locale: fr });
  if (!end || !isValid(end) || isSameDay(start, end)) {
    return interpolateLabel(labels.dateSameDay, { date: startDate });
  }
  const endDate = format(end, 'd MMM', { locale: fr });
  const startDay = format(start, 'd', { locale: fr });
  return interpolateLabel(labels.dateRange, { start: startDay, end: endDate });
}

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {Array<{ title: string, type: ApiManagerTimelineResourceType | null, section: string | null }>}
 */
function getEventResources(event) {
  if (typeof event.getResources !== 'function') {
    return [];
  }

  return event.getResources()
    .filter((/** @type {CalendarResource | null} */ resource) => resource !== null && resource.title)
    .map((/** @type {CalendarResource} */ resource) => ({
      title: resource.title,
      type: resource.extendedProps?.type ?? null,
      section: resource.extendedProps?.section ?? resource.section ?? null,
    }));
}

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @param {EventFocusLabels} labels
 * @returns {EventFocusViewModel | null}
 */
export function buildEventFocusViewModel(event, labels) {
  if (!event?.start || !isValid(event.start)) {
    return null;
  }

  const resources = getEventResources(event);
  const resourcesByType = {
    LIEU: resources.filter((resource) => resource.type === 'LIEU'),
    PERSONNE: resources.filter((resource) => resource.type === 'PERSONNE'),
    VEHICULE: resources.filter((resource) => resource.type === 'VEHICULE'),
    AUTRE: resources.filter((resource) => resource.type === 'AUTRE' || resource.type === null),
  };

  /** @type {EventFocusRow[]} */
  const rows = [];

  const clockInstants = resolveEventFocusClockInstants(event);
  const showClockRow = !event.allDay || event.extendedProps?.displayStart;
  if (showClockRow && clockInstants.start) {
    const startTime = formatEventFocusClockTime(clockInstants.start);
    const endTime = formatEventFocusClockTime(clockInstants.end);
    const timeText = endTime && endTime !== startTime
      ? interpolateLabel(labels.timeRange, { start: startTime, end: endTime })
      : startTime;

    if (timeText) {
      rows.push({
        type: 'text',
        icon: 'clock',
        text: timeText,
      });
    }
  }

  if (resourcesByType.LIEU.length > 0) {
    rows.push({
      type: 'text',
      icon: 'map',
      text: resourcesByType.LIEU.map((resource) => resource.title).join(', '),
    });
  }

  if (resourcesByType.PERSONNE.length > 0) {
    rows.push({
      type: 'list',
      icon: 'info-circle',
      label: labels.participant,
      items: resourcesByType.PERSONNE.map((resource) => ({
        title: resource.title,
        ...(resource.section ? { subtitle: resource.section } : {}),
      })),
    });
  }

  if (resourcesByType.VEHICULE.length > 0) {
    rows.push({
      type: 'text',
      icon: 'info-circle',
      text: resourcesByType.VEHICULE.map((resource) => resource.title).join(', '),
    });
  }

  if (resourcesByType.AUTRE.length > 0) {
    rows.push({
      type: 'text',
      icon: 'info-circle',
      text: resourcesByType.AUTRE.map((resource) => resource.title).join(', '),
    });
  }

  const recurrence = event.extendedProps?.recurrence;
  if (recurrence?.type) {
    const recurrenceText = recurrence.until
      ? `${recurrence.type} — ${recurrence.until}`
      : recurrence.type;
    rows.push({
      type: 'text',
      icon: 'calendar',
      text: `${labels.recurrence} : ${recurrenceText}`,
    });
  }

  return {
    header: {
      title: event.title,
      dateLabel: formatEventFocusDateLabel(event.start, event.end, labels),
      icon: event.extendedProps?.icon ?? null,
    },
    rows,
  };
}

/** @typedef {'openFormModal' | 'delete' | 'duplicate' | 'openTab'} TimelineEventContextMenuActionType */

/**
 * @typedef {object} TimelineEventContextMenuItem
 * @property {string} id
 * @property {TimelineEventContextMenuActionType} type
 * @property {string} lib
 * @property {string} icon
 * @property {string} url
 */

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {string}
 */
function getPrimaryResourceIdFromEvent(event) {
  const resources = event.getResources?.() ?? [];
  return resources[0]?.id ?? '';
}

/**
 * Wall-clock time from a FullCalendar Date (for form contexte prefill, not API instants).
 * @param {Date | null} date
 * @returns {string}
 */
function formatTimelineLocalDateTime(date) {
  if (!date || !isValid(date)) {
    return '';
  }
  return format(date, 'yyyy-MM-dd\'T\'HH:mm:ss');
}

/**
 * Serialize an event instant as wall-clock digits plus +00:00, matching /timeline GET.
 * @param {Date | null} date
 * @returns {string}
 */
function formatTimelineEventDateTime(date) {
  const local = formatTimelineLocalDateTime(date);
  if (!local) {
    return '';
  }
  return `${local}+00:00`;
}

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {ApiManagerTimelineRecurrence | null}
 */
export function getEventRecurrence(event) {
  return event.extendedProps?.recurrence ?? null;
}

/**
 * @param {import('@fullcalendar/core').EventDropArg | import('@fullcalendar/interaction').EventResizeDoneArg} info
 * @param {ApiManagerRecurrenceEditionScope} [recurrenceEditionScope='none']
 * @returns {{ fastEditUrl: string, eventId: string, payload: ApiManagerFastEditEventCreate } | null}
 */
export function buildFastEditPayload(info, recurrenceEditionScope = 'none') {
  const { event } = info;
  const fastEditUrl = event.extendedProps?.fastEditUrl;
  if (!fastEditUrl) {
    return null;
  }

  const { start: startDate, end: endDate } = resolveFastEditEventInstants(event);
  const fallbackResourceId = getPrimaryResourceIdFromEvent(event);
  const isDrop = 'oldResource' in info;
  const fromResourceId = isDrop && info.oldResource?.id
    ? info.oldResource.id
    : fallbackResourceId;
  const toResourceId = isDrop && info.newResource?.id
    ? info.newResource.id
    : fromResourceId;

  return {
    fastEditUrl,
    eventId: event.id,
    payload: {
      start: formatTimelineEventDateTime(startDate),
      end: formatTimelineEventDateTime(endDate),
      fromResourceId,
      toResourceId,
      recurrenceEditionScope,
    },
  };
}

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {string | null}
 */
export function getEventOpenFormModalUrl(event) {
  const actions = event.extendedProps?.actions ?? [];
  const withForm = actions.find((actionObj) => actionObj?.openFormModal?.formUrl);
  return withForm?.openFormModal?.formUrl ?? null;
}

/** @type {Array<{ key: string, type: TimelineEventContextMenuActionType, urlKey: string }>} */
const TIMELINE_CONTEXT_MENU_ACTION_SPECS = [
  { key: 'openFormModal', type: 'openFormModal', urlKey: 'formUrl' },
  { key: 'delete', type: 'delete', urlKey: 'deleteUrl' },
  { key: 'duplicate', type: 'duplicate', urlKey: 'duplicateUrl' },
  { key: 'openTab', type: 'openTab', urlKey: 'basicUrl' },
];

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {TimelineEventContextMenuItem[]}
 */
export function buildEventContextMenuItems(event) {
  const actionsList = event.extendedProps?.actions ?? [];
  /** @type {TimelineEventContextMenuItem[]} */
  const items = [];

  actionsList.forEach((actionObj, actionIndex) => {
    TIMELINE_CONTEXT_MENU_ACTION_SPECS.forEach(({ key, type, urlKey }) => {
      const action = actionObj?.[key];
      if (!action || typeof action !== 'object') {
        return;
      }
      const url = action[urlKey];
      if (!url) {
        return;
      }
      items.push({
        id: `${type}-${actionIndex}-${action.lib}`,
        type,
        lib: action.lib,
        icon: action.icon,
        url,
      });
    });
  });

  return items;
}

/**
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {string | null}
 */
export function getEventDuplicateUrl(event) {
  const actions = event.extendedProps?.actions ?? [];
  const withDuplicate = actions.find((actionObj) => actionObj?.duplicate?.duplicateUrl);
  return withDuplicate?.duplicate?.duplicateUrl ?? null;
}

/**
 * @typedef {object} TimelineDateSelection
 * @property {Date} start
 * @property {Date} end
 * @property {string | null} resourceId
 */

/**
 * @param {string} addFormUrl - url from addFormUrls (may already include back contexte)
 * @param {TimelineDateSelection} selection
 * @returns {string}
 */
export function buildEventCreationFormUrl(addFormUrl, selection) {
  const resourceId = selection.resourceId ?? '';
  return appendContexteValuesToUrl(addFormUrl, {
    start: formatTimelineLocalDateTime(selection.start),
    end: formatTimelineLocalDateTime(selection.end),
    resourceId,
  });
}
